import React, { useEffect, useState } from "react";
import { AiOutlineWallet } from "react-icons/ai";
import Button from "../../components/common/button";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ActionCard from "../../components/wallet/actionCard";
import { withdrawOptionsTemplate } from "../../data/wallet";
import { addMoney, fetchWalletData } from "../../redux/slices/walletSlice";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import { useInitializeRazorpay } from "../../utilities/razorpay/paymentGateway";
import LastFiveTransactions from "../../components/lastFiveTransactions";
import { fetchPaymentTransactionsThunk } from "../../redux/slices/paymentSlice";
import BackdropSpinner from "../../components/backdropSpinner";

function Wallet() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const initializeRazorpay = useInitializeRazorpay();
  const [isDisabled, setIsDisabled] = useState(false);
  const [amount, setAmount] = useState("");
  const [loading2, setLoading] = useState(false);
  const { balance, loading } = useSelector((state) => state.wallet);

  //  Withdrawal options with translations
  const withdrawOptions = withdrawOptionsTemplate.map((option) => ({
    ...option,
    altText: t(option.altTextKey),
    title: t(option.titleKey),
  }));

  // Decode user data from local storage
  const decodedData = JSON.parse(localStorage.getItem("decodedData"));
  const { id } = decodedData;

  const isPaymentVerified = localStorage.getItem("is_payment_verified")

  const handleWithdraw = () => {
    navigate("/withdraw");
  };

  const handleHistory = () => {
    navigate("/withdraw-request");
  };

  // Handle input change for amount field
  const handleAmountChange = (e) => {
    // Get the value from the input
    const value = e.target.value;

    // Ensure the value is an integer and not empty
    if (value === "" || /^[0-9]*$/.test(value)) {
      setAmount(value); // Assuming you're using a state setter like useState
    }
  };
  // Handle adding money to the wallet
  const handleAdd = async () => {
    try {
      if (!amount || parseFloat(amount) <= 9) {
        toast.error(t("wallet.errors.invalidAmount"));
        return;
      }

      if (parseFloat(amount) > 500000) {
        toast.error(t("wallet.errors.maxAmountExceeded"));
        setIsDisabled(true);
        setTimeout(() => {
          setIsDisabled(false); // Re-enable button after 2 seconds
        }, 3000);
        return;
      }

      setLoading(true);

      const payload = {
        user_id: id,
        wallet_balance: amount,
      };

      // Dispatch action to initiate adding money
      const response = await dispatch(addMoney(payload)).unwrap();
      // Initialize Razorpay if order creation is successful
      if (response.data && response.data.order_id) {
        await initializeRazorpay(
          response.data.order_id,
          response.data.amount,
          id,
          balance
        );
        dispatch(fetchPaymentTransactionsThunk(id));
        setAmount("");
      } else {
        toast.error(t("wallet.errors.initializePaymentFailed"));
      }
    } catch (error) {
      console.error("Error adding money:", error);
      toast.error(t("wallet.errors.genericError"));
    }
    setLoading(false);
  };

  useEffect(() => {
    if (id && balance) {
      dispatch(fetchPaymentTransactionsThunk(id));
    }
  }, [dispatch, id, balance]);

  useEffect(() => {
    // && isNaN(balance)
    if (id) {
      dispatch(fetchWalletData(id)); // Fetch wallet data on component mount
    }
  }, [id, dispatch]);

  if (loading) {
    return <BackdropSpinner />;
  }

  return (
    <div className="flex flex-col items-center w-full px-4 lg:px-0">
      {/* Wallet Header */}
      <div className="flex justify-center">
        <div className="flex flex-col w-full max-w-lg mt-5 ">
          <div className="flex items-center mb-6">
            <AiOutlineWallet className="text-[#FFBF4D] w-6 h-6 mr-3" />
            <div>
              <p className="text-white text-lg font-medium">
                {t("wallet.header.title2")}
              </p>
              <div className="flex items-center space-x-2">
                <span>{t("wallet.currentBalance")}</span>
                {isNaN(balance) ? (
                  <span className="flex space-x-1">
                    <span className="w-[6px] h-[6px] bg-white rounded-full animate-bounce delay-300"></span>
                    <span className="w-[6px] h-[6px] bg-white rounded-full animate-bounce delay-700"></span>
                    <span className="w-[6px] h-[6px] bg-white rounded-full animate-bounce delay-400"></span>
                  </span>
                ) : (
                  <span>₹ {balance}</span>
                )}
              </div>

              <p className="text-white/50 text-sm">
                {t("wallet.header.description")}
              </p>
            </div>
          </div>

          {/* Conditionally Render Input Section and Upload Button */}
          {isPaymentVerified !== 'false' && (
            <>
              <div className="flex flex-col mb-6">
                <label className="text-white text-sm font-medium" htmlFor="amount">
                  {t("wallet.input.label")}
                </label>
                <input
                  id="amount"
                  type="text"
                  inputMode="numeric"
                  value={amount}
                  placeholder="0"
                  className="border border-[#E2A32C] rounded-md w-[340px]  placeholder:text-white/30 py-2 px-4 lg:w-full sm:w-2/3 md:w-1/3 text-white placeholder-white bg-transparent focus:outline-none focus:ring-0"
                  onChange={handleAmountChange}
                />
                <p className="text-white/50 text-sm">{t("wallet.input.minimum")}</p>
              </div>

              <div className="flex justify-center mb-7">
                <Button
                  text={loading2 ? t("wallet.loading") : t("wallet.uploadButton")}
                  onClick={handleAdd}
                  disabled={isDisabled}
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Payment Methods */}
      {/* <div className="w-full max-w-lg">
          <p className="text-white text-lg font-medium mb-4">
            {t("wallet.paymentMethods")}
          </p>
          <div className="grid grid-cols-2 gap-4">
            {paymentMethods.map((method, index) => (
              <PaymentMethodCard key={index} {...method} />
            ))}
          </div>
        </div> */}

      <div className="flex justify-center w-full max-w-lg">
        <div className="w-full">
          <LastFiveTransactions userId={id} />
        </div>
      </div>

      {/* Withdraw Options */}
      <div className="w-full max-w-lg grid grid-cols-2 gap-4 my-4">
        {withdrawOptions.map((option, index) => (
          <ActionCard
            key={index}
            {...option}
            handleHistory={handleHistory}
            handleWithdraw={handleWithdraw}
          />
        ))}
      </div>

      {/* Footer Options */}
      {/* <div className="flex items-center w-full max-w-lg my-4">
          <div className="w-10 h-10 bg-white/70 rounded-full mr-4" />
          <div>
            <p className="text-white text-lg font-medium">
              {t("wallet.options.back")}
            </p>
            <p className="text-white/50 text-sm">
              {t("wallet.options.description")}
            </p>
          </div>
        </div> */}
    </div>
  );
}

export default Wallet;
